import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Tabs } from 'src/design/molecules/Tabs';
import { Header } from 'src/design/organisms/Header';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { useJobs } from 'src/hooks/useJobs';
import {
  ChangeForm,
  ChangeFormSection,
  ChangeFormValues,
  WorkflowContactDto,
} from 'src/routes/Jobs/Changes/components/ChangeForm';
import { NotificationsTab } from 'src/app/workflows/NotificationsTab';
import { ChangeHistoryDto, HistoryList } from 'src/app/workflows/HistoryList';
import { useChange } from 'src/api/queries/changes/useChangeQueries';
import { parseExternalContact } from 'src/app/workflows/parseExternalContact';

export const ViewChange: React.FC = () => {
  const { selectedJob, users } = useJobs();
  const navigate = useNavigate();

  const params = useParams<{ changeId: string }>();
  const [tab, setTab] = React.useState<string>('details');
  const {
    data: change,
    isLoading: loading,
    isError,
  } = useChange(selectedJob?.id, params.changeId);

  const changeNumber = change?.changeNumber || null;
  const submittedToContact: WorkflowContactDto | undefined =
    change?.submittedToContact;
  const history: ChangeHistoryDto[] = change?.history ?? [];

  const values = React.useMemo<ChangeFormValues | null>(() => {
    if (!change) return null;
    return {
      id: change.id,
      date: change.date,
      description: change.description,
      status: change.status,
      impactNotes: change.impactNotes,
      assigneeUserId: change.assigneeUserId,
      stepIndex: change.stepIndex,
      workflowId: change.workflowId,
      externalContact: parseExternalContact(change.externalContact),
      submittedToContactId: change.submittedToContactId,
      submittedByCompany: change.submittedByCompany,
      changeTypeId: change.changeTypeId,
      changeTypeName: change.changeTypeName,
      proposedAmount: change.proposedAmount,
      approvedAmount: change.approvedAmount,
      proposedDays: change.proposedDays,
      approvedDays: change.approvedDays,
      attachments: change.attachments || [],
      filesToAdd: [],
      filesToRemove: [],
      workflow: change.workflow || null,
      saveOnly: false,
    };
  }, [change]);

  if (!selectedJob || !params.changeId) return null;
  return (
    <>
      <Header
        breadcrumbProps={{
          items: jobBreadcrumbs(
            selectedJob,
            { text: 'Changes', href: `/jobs/${selectedJob.id}/changes` },
            changeNumber || 'View Change',
          ),
        }}
        title={changeNumber || 'View Change'}
        helpText="View and manage this change order request."
      />
      <Group justify="flex-end" mb="md">
        <SecondaryButton
          onClick={() => navigate(`/jobs/${selectedJob.id}/changes`)}
        >
          Cancel
        </SecondaryButton>
      </Group>
      {isError && <p className="text-center">Unable to load this change.</p>}
      {loading || !values ? (
        <div className="text-center">
          <Loader />
        </div>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={(value) => setTab(value || 'details')}
            mt="md"
          >
            <Tabs.List>
              <Tabs.Tab value="details">Details</Tabs.Tab>
              <Tabs.Tab value="attachments">Attachments</Tabs.Tab>
              <Tabs.Tab value="signed">Signed Documents</Tabs.Tab>
              <Tabs.Tab value="notifications">Notifications</Tabs.Tab>
              <Tabs.Tab value="history">History</Tabs.Tab>
            </Tabs.List>
          </Tabs>

          <Box mt="md">
            <ChangeForm
              initialValues={values}
              changeId={params.changeId}
              submittedToContact={submittedToContact}
              jobUsers={users}
              activeSection={tab as ChangeFormSection}
            >
              {tab === 'notifications' && (
                <NotificationsTab
                  resourceType="change"
                  entityNoun="change"
                  attachPdfLabel="Attach change PDF"
                  jobId={selectedJob.id}
                  entityId={params.changeId}
                  jobUsers={users}
                  customerNumber={selectedJob.customerNumber?.trim() ?? ''}
                />
              )}

              {tab === 'history' && <HistoryList history={history} />}
            </ChangeForm>
          </Box>
        </>
      )}
    </>
  );
};
